import type { User } from '@/shared/types/userTypes';
import { OrgType, Role, parseOrgRole, toOrgRole, type OrgRole } from '@/shared/types/userRoleTypes';

/** Infer org type from `/me` user profile shape. */
export function deriveOrgTypeFromUser(user: User): OrgType | null {
  if ((user.availableRegions?.length ?? 0) > 0) {
    return OrgType.PLATFORM;
  }

  const organizations = user.organizations ?? [];
  if (organizations.length === 0) {
    return null;
  }

  if (organizations.some((organization) => (organization.parentOrganizations?.length ?? 0) > 0)) {
    return OrgType.CUSTOMER;
  }

  return OrgType.TENANT;
}

export function isPlatformUserProfile(user: User | null | undefined): boolean {
  return !!user
    && deriveOrgTypeFromUser(user) === OrgType.PLATFORM
    && (user.availableRegions?.length ?? 0) > 0;
}

export function isTenantUserProfile(user: User | null | undefined): boolean {
  return !!user
    && deriveOrgTypeFromUser(user) === OrgType.TENANT
    && (user.organizations?.length ?? 0) > 0
    && user.organizations?.some((organization) => (organization.parentOrganizations?.length ?? 0) === 0);
}

export function isCustomerUserProfile(user: User | null | undefined): boolean {
  return !!user
    && deriveOrgTypeFromUser(user) === OrgType.CUSTOMER
    && (user.organizations?.length ?? 0) > 0
    && user.organizations?.some((organization) => (organization.parentOrganizations?.length ?? 0) > 0);
}

export function resolveUserRole(user: User): Role | null {
  if (user.role && Object.values(Role).includes(user.role)) {
    return user.role;
  }

  if (user.role) {
    const compositeRole = parseOrgRole(String(user.role));
    if (compositeRole) {
      return compositeRole.slice(compositeRole.indexOf('_') + 1) as Role;
    }
  }

  const claimsRole = user.claims?.role;
  if (claimsRole && Object.values(Role).includes(claimsRole)) {
    return claimsRole;
  }

  const defaultOrganization = user.organizations?.find((organization) => organization.isDefault)
    ?? user.organizations?.[0];
  if (defaultOrganization?.role && Object.values(Role).includes(defaultOrganization.role)) {
    return defaultOrganization.role;
  }

  if (deriveOrgTypeFromUser(user) === OrgType.PLATFORM) {
    return user.canAccessGlobalDashboard ? Role.ADMIN : Role.VIEWER;
  }

  return null;
}

export function deriveOrgRoleFromUser(user: User): OrgRole | null {
  const orgType = user.claims?.orgType ?? deriveOrgTypeFromUser(user);
  const role = resolveUserRole(user);
  if (!orgType || !role) {
    return null;
  }

  try {
    return toOrgRole(orgType, role);
  } catch {
    return null;
  }
}
